// Deterministic Speaker Color Assignment Utility
// Ensures consistent color mapping for the same speakers across all components

// Comprehensive 50-color palette for speaker assignments
export const SPEAKER_COLOR_PALETTE = [
    // Primary colors (1-10)
    "emerald-500", "blue-500", "purple-500", "red-500", "yellow-500", 
    "lime-500", "pink-500", "indigo-500", "orange-500", "teal-500",
    
    // Secondary vibrant colors (11-20)
    "cyan-500", "violet-500", "rose-500", "amber-500", "green-500",
    "sky-500", "fuchsia-500", "orange-400", "emerald-600", "blue-600",
    
    // Darker variants (21-30)
    "purple-600", "red-600", "yellow-600", "lime-600", "pink-600",
    "indigo-600", "teal-600", "cyan-600", "violet-600", "rose-600",
    
    // Lighter variants (31-40)
    "emerald-400", "blue-400", "purple-400", "red-400", "yellow-400",
    "lime-400", "pink-400", "indigo-400", "orange-300", "teal-400",
    
    // Additional unique colors (41-50)
    "slate-500", "gray-500", "zinc-500", "neutral-500", "stone-500",
    "amber-600", "green-600", "sky-600", "fuchsia-600", "rose-700"
];

/**
 * Simple hash function to convert string to number for consistent color assignment
 * @param {string} str - Speaker name or identifier
 * @returns {number} - Hash value
 */
function simpleHash(str) {
    if (typeof str !== 'string') {
        str = String(str || '');
    }
    
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
}

/**
 * Get deterministic color for a speaker based on their name/ID
 * Same speaker will always get the same color across all components
 * @param {string} speakerIdentifier - Speaker name, ID, or key
 * @param {number} cycleNumber - For large datasets, which color cycle to use (0-3)
 * @returns {string} - Tailwind color class
 */
export function getDeterministicSpeakerColor(speakerIdentifier, cycleNumber = 0) {
    if (!speakerIdentifier) {
        return 'gray-500';
    }
    
    // Clean the identifier for consistent hashing
    const cleanIdentifier = String(speakerIdentifier).trim().toLowerCase();
    
    // Get hash-based index
    const hash = simpleHash(cleanIdentifier);
    const baseIndex = hash % SPEAKER_COLOR_PALETTE.length;
    const baseColor = SPEAKER_COLOR_PALETTE[baseIndex];
    
    // Apply cycle variations for large datasets
    if (cycleNumber === 0) {
        return baseColor; // Original color
    } else if (cycleNumber === 1) {
        return baseColor.replace('-500', '-400'); // Lighter
    } else if (cycleNumber === 2) {
        return baseColor.replace('-500', '-600'); // Darker  
    } else {
        return baseColor.replace('-500', '-300'); // Much lighter
    }
}

/**
 * Get colors for an array of speakers, ensuring consistency
 * @param {Array} speakers - Array of speaker identifiers
 * @returns {Array} - Array of color classes
 */
export function getDeterministicSpeakerColors(speakers) {
    if (!Array.isArray(speakers)) {
        return [];
    }
    
    return speakers.map((speaker, index) => {
        // Determine which color cycle to use for speakers beyond the base palette
        const cycleNumber = Math.floor(index / SPEAKER_COLOR_PALETTE.length);
        return getDeterministicSpeakerColor(speaker, cycleNumber);
    });
}

/**
 * Create a consistent speaker-to-color mapping object
 * @param {Array} speakers - Array of speaker identifiers  
 * @returns {Object} - Mapping of speaker -> color
 */
export function createSpeakerColorMapping(speakers) {
    if (!Array.isArray(speakers)) {
        return {};
    }
    
    const mapping = {};
    speakers.forEach((speaker, index) => {
        const cycleNumber = Math.floor(index / SPEAKER_COLOR_PALETTE.length);
        mapping[speaker] = getDeterministicSpeakerColor(speaker, cycleNumber);
    });
    
    return mapping;
}